"use client"

import React, { useState, useRef, useEffect } from "react"
import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { GEMINI_API_URL, GEMINI_API_KEY } from "../config"

interface Message {
  type: "user" | "assistant"
  content: string
  time: string
  media?: {
    type: string
    url: string
    name: string
    preview?: string 
  }[]
}

type ContentPart =
  | { type: "text"; content: string | InlineFormattedPart[] }
  | { type: "codeblock"; language: string; content: string }
  | { type: "bullet"; content: string | InlineFormattedPart[]; indentClass: string }

type InlineFormattedPart =
  | { type: "inlinecode"; content: string }
  | { type: "bold"; content: string }
  | { type: "italic"; content: string }
  | { type: "text"; content: string }

interface GameState {
  bird: { x: number; y: number; velocity: number }
  pipes: { x: number; height: number }[]
  score: number
  gameOver: boolean
  gameStarted: boolean
}

const LLM = (props) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attachments, setAttachments] = useState<
    {
      file: File
      preview: string | null
      type: string
    }[]
  >([])
  const [showCamera, setShowCamera] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment")
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [showGame, setShowGame] = useState(false)
  const [humanizeMode, setHumanizeMode] = useState(false)
  const [deepSearchMode, setDeepSearchMode] = useState(false)
  const [model, setModel] = useState<"gemini" | "deepseek" | "v0small">("gemini")

  const [modelNotification, setModelNotification] = useState<string | null>(null)

  const [showTerminalPopup, setShowTerminalPopup] = useState(false)
  const [terminalOutput, setTerminalOutput] = useState("")
  const [terminalError, setTerminalError] = useState<string | null>(null)
  const [isTerminalRunning, setIsTerminalRunning] = useState(false)
  const [executingLanguage, setExecutingLanguage] = useState<string | null>(null) 

  const [activeFiles, setActiveFiles] = useState<{ name: string; language: string; content: string }[]>([])
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [showLiveOutput, setShowLiveOutput] = useState(true) 
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const gameCanvasRef = useRef<HTMLCanvasElement>(null)
  const gameLoopRef = useRef<number | null>(null)
  const navigate = useNavigate()

  const gameStateRef = useRef<GameState>({
    bird: { x: 100, y: 250, velocity: 0 },
    pipes: [],
    score: 0,
    gameOver: false,
    gameStarted: false,
  })

  const saveMessages = (msgs: Message[]) => {
    localStorage.setItem("gemini-chat-messages", JSON.stringify(msgs))
  }

  const loadMessages = (): Message[] => {
    const saved = localStorage.getItem("gemini-chat-messages")
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return []
      }
    }
    return []
  }

  useEffect(() => {
    setMessages(loadMessages())
  }, [])

  useEffect(() => {
    saveMessages(messages)
  }, [messages])

  const clearChat = () => {
    setMessages([])
    localStorage.removeItem("gemini-chat-messages")
  }

  const replyToAI = (aiMessageIndex: number) => {
    const aiMessage = messages[aiMessageIndex]
    if (!aiMessage || aiMessage.type !== "assistant") return
    setInput(`Replying to: "${aiMessage.content.slice(0, 60)}"\n`)
  }

  useEffect(() => {
    const savedMessages = localStorage.getItem("gemini-chat-messages")
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages)
        setMessages(parsedMessages)
      } catch (error) {
        console.error("Error loading saved messages:", error)
      }
    }
  }, [])

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("gemini-chat-messages", JSON.stringify(messages))
    }
  }, [messages])

  const renderGame = () => {
    if (!gameCanvasRef.current) return

    const canvas = gameCanvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const gameState = gameStateRef.current

    ctx.fillStyle = "#87CEEB"
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.fillStyle = "rgba(255, 255, 255, 0.8)"
    ctx.beginPath()
    ctx.arc(100, 80, 30, 0, Math.PI * 2)
    ctx.arc(130, 90, 25, 0, Math.PI * 2)
    ctx.arc(160, 80, 20, 0, Math.PI * 2)
    ctx.fill()

    ctx.beginPath()
    ctx.arc(400, 120, 35, 0, Math.PI * 2)
    ctx.arc(440, 130, 30, 0, Math.PI * 2)
    ctx.arc(480, 120, 25, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = "#FFD700"
    ctx.beginPath()
    ctx.ellipse(gameState.bird.x + 10, gameState.bird.y + 10, 15, 10, gameState.bird.velocity * 0.05, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = "#000"
    ctx.beginPath()
    ctx.arc(gameState.bird.x + 15, gameState.bird.y + 7, 2, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = "#FF6600"
    ctx.beginPath()
    ctx.moveTo(gameState.bird.x + 20, gameState.bird.y + 10)
    ctx.lineTo(gameState.bird.x + 28, gameState.bird.y + 8)
    ctx.lineTo(gameState.bird.x + 20, gameState.bird.y + 14)
    ctx.fill()

    gameState.pipes.forEach((pipe) => {
      ctx.fillStyle = "#228B22"
      ctx.fillRect(pipe.x, 0, 60, pipe.height)
      ctx.fillStyle = "#32CD32"
      ctx.fillRect(pipe.x - 5, pipe.height - 20, 70, 20)

      ctx.fillStyle = "#228B22"
      ctx.fillRect(pipe.x, pipe.height + 200, 60, canvas.height - pipe.height - 200)
      ctx.fillStyle = "#32CD32"
      ctx.fillRect(pipe.x - 5, pipe.height + 200, 70, 20)
    })

    ctx.fillStyle = "#8B4513"
    ctx.fillRect(0, 480, canvas.width, 20)
    ctx.fillStyle = "#A0522D"
    for (let i = 0; i < canvas.width; i += 30) {
      ctx.fillRect(i, 485, 15, 5)
    }

    ctx.fillStyle = "#FFF"
    ctx.font = "bold 24px Arial"
    ctx.fillText(`Score: ${gameState.score}`, 20, 40)

    if (gameState.gameOver) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)"
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.fillStyle = "#FFF"
      ctx.font = "bold 36px Arial"
      ctx.textAlign = "center"
      ctx.fillText("Game Over!", canvas.width / 2, canvas.height / 2 - 40)
      ctx.font = "bold 24px Arial"
      ctx.fillText(`Final Score: ${gameState.score}`, canvas.width / 2, canvas.height / 2)
      ctx.font = "bold 18px Arial"
      ctx.fillText("Click to restart", canvas.width / 2, canvas.height / 2 + 40)
      ctx.textAlign = "left"
    }

    if (!gameState.gameStarted && !gameState.gameOver) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)"
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.fillStyle = "#FFF"
      ctx.font = "bold 36px Arial"
      ctx.textAlign = "center"
      ctx.fillText("Flappy Bird", canvas.width / 2, canvas.height / 2 - 40)
      ctx.font = "bold 18px Arial"
      ctx.fillText("Press SPACE or Click to start", canvas.width / 2, canvas.height / 2 + 20)
      ctx.textAlign = "left"
    }
  }

  const updateGame = () => {
    const gameState = gameStateRef.current

    if (!gameState.gameStarted || gameState.gameOver) return

    gameState.bird.velocity += 0.25 
    gameState.bird.y += gameState.bird.velocity

    if (gameState.bird.velocity > 8) {
      gameState.bird.velocity = 8
    }

    gameState.pipes = gameState.pipes
      .map((pipe) => ({
        ...pipe,
        x: pipe.x - 1.5, 
      }))
      .filter((pipe) => pipe.x > -100)

    if (gameState.pipes.length === 0 || gameState.pipes[gameState.pipes.length - 1].x < 300) {
      gameState.pipes.push({
        x: 600,
        height: Math.random() * 120 + 120,
      })
    }

    const bird = gameState.bird
    const birdSize = 20
    const hitboxReduction = 8 
    if (bird.y > 460 - birdSize || bird.y < 10) {
      gameState.gameOver = true
    }

    gameState.pipes.forEach((pipe) => {
      if (
        bird.x + birdSize - hitboxReduction > pipe.x + 5 &&
        bird.x + hitboxReduction < pipe.x + 55 &&
        (bird.y + hitboxReduction < pipe.height || bird.y + birdSize - hitboxReduction > pipe.height + 200)
      ) {
        gameState.gameOver = true
      }

      if (pipe.x + 60 < bird.x && pipe.x + 60 > bird.x - 2) {
        gameState.score += 1
      }
    })
  }

  const gameLoop = () => {
    if (!showGame) return

    updateGame()
    renderGame()

    if (gameStateRef.current.gameStarted && !gameStateRef.current.gameOver) {
      gameLoopRef.current = requestAnimationFrame(gameLoop)
    }
  }

  useEffect(() => {
    if (showGame) {
      gameLoopRef.current = requestAnimationFrame(gameLoop)
    }

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current)
      }
    }
  }, [showGame])

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (showGame && (e.code === "Space" || e.code === "ArrowUp")) {
        e.preventDefault()
        const gameState = gameStateRef.current

        if (!gameState.gameStarted) {
          gameState.gameStarted = true
          gameLoopRef.current = requestAnimationFrame(gameLoop)
        } else if (!gameState.gameOver) {
          gameState.bird.velocity = -5 
        } else {
          resetGame()
        }
      }
    }

    window.addEventListener("keydown", handleKeyPress)
    return () => window.removeEventListener("keydown", handleKeyPress)
  }, [showGame])

  const handleGameClick = () => {
    const gameState = gameStateRef.current

    if (!gameState.gameStarted) {
      gameState.gameStarted = true
      gameLoopRef.current = requestAnimationFrame(gameLoop)
    } else if (!gameState.gameOver) {
      gameState.bird.velocity = -5
    }
  }

  const resetGame = () => {
    gameStateRef.current = {
      bird: { x: 100, y: 250, velocity: 0 },
      pipes: [],
      score: 0,
      gameOver: false,
      gameStarted: false,
    }
    renderGame() 
  }

  useEffect(() => {
    if (showGame) {
      renderGame()
    }
  }, [showGame])

  const getCurrentTime = () => {
    const now = new Date()
    const hours = now.getHours()
    const minutes = now.getMinutes()
    const ampm = hours >= 12 ? "PM" : "AM"
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes
    return `${formattedHours}:${formattedMinutes} ${ampm}`
  }

  const parseMessageContent = (content: string): ContentPart[] => {
    if (!content || typeof content !== "string") {
      return [{ type: "text", content: content || "" }]
    }

    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g
    const parts: ContentPart[] = []
    let lastIndex = 0
    let match

    while ((match = codeBlockRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        const textBefore = content.slice(lastIndex, match.index)
        if (textBefore.trim()) {
          parts.push(...parseTextContent(textBefore))
        }
      }

      parts.push({
        type: "codeblock",
        language: match[1] || "text",
        content: match[2].trim(),
      })

      lastIndex = match.index + match[0].length
    }

    if (lastIndex < content.length) {
      const remainingText = content.slice(lastIndex)
      if (remainingText.trim()) {
        parts.push(...parseTextContent(remainingText))
      }
    }

    return parts.length > 0 ? parts : [{ type: "text", content }]
  }

  const parseTextContent = (text: string): ContentPart[] => {
    const parts: ContentPart[] = []
    const lines = text.split("\n")

    for (const line of lines) {
      if (!line.trim()) {
        parts.push({ type: "text", content: "\n" })
        continue
      }

      const bulletMatch = line.match(/^(\s*)\*\s+(.+)$/)
      if (bulletMatch) {
        const indentation = bulletMatch[1].length
        const bulletContent = bulletMatch[2]
        const indentClass = indentation > 0 ? `ml-${indentation * 4}` : ""

        const formattedContent = parseInlineFormatting(bulletContent)
        parts.push({
          type: "bullet",
          content: formattedContent,
          indentClass,
        })
      } else {
        const formattedContent = parseInlineFormatting(line)
        parts.push({ type: "text", content: formattedContent })
      }

      if (line !== lines[lines.length - 1]) {
        parts.push({ type: "text", content: "\n" })
      }
    }

    return parts
  }

  const parseInlineFormatting = (text: string): InlineFormattedPart[] => {
    const parts: InlineFormattedPart[] = []
    let currentIndex = 0

    const patterns = [
      { type: "inlinecode", regex: /`([^`]+)`/g },
      { type: "bold", regex: /\*\*([^*]+?)\*\*/g },
      { type: "italic", regex: /\*([^*\n]+?)\*/g },
    ]

    const allMatches: { type: string; start: number; end: number; content: string; fullMatch: string }[] = []

    for (const pattern of patterns) {
      let match
      const regex = new RegExp(pattern.regex.source, pattern.regex.flags)
      while ((match = regex.exec(text)) !== null) {
        allMatches.push({
          type: pattern.type,
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          fullMatch: match[0],
        })
      }
    }

    allMatches.sort((a, b) => a.start - b.start)

    for (const match of allMatches) {
      if (match.start > currentIndex) {
        const textBefore = text.slice(currentIndex, match.start)
        if (textBefore) {
          parts.push({ type: "text", content: textBefore })
        }
      }

      parts.push({ type: match.type as InlineFormattedPart['type'], content: match.content })
      currentIndex = match.end
    }

    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex)
      if (remainingText) {
        parts.push({ type: "text", content: remainingText })
      }
    }

    if (parts.length === 0) {
      return [{ type: "text", content: text }]
    }

    return parts
  }

  const copyToClipboard = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(id)
      setTimeout(() => setCopiedCode(null), 2000) 
    } catch (err) {
      console.error("Failed to copy code:", err)
      const textArea = document.createElement("textarea")
      textArea.value = code
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
      setCopiedCode(id)
      setTimeout(() => setCopiedCode(null), 2000)
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isTyping])

  useEffect(() => {
    checkAvailableModels()
  }, [])

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => {
          track.stop()
        })
      }
    }
  }, [cameraStream])

  useEffect(() => {
    if (showCamera) {
      initializeCamera()
    } else if (cameraStream) {
      cameraStream.getTracks().forEach((track) => {
        track.stop()
      })
      setCameraStream(null)
    }
  }, [showCamera, facingMode])

  const checkAvailableModels = async () => {
    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1/models?key=${GEMINI_API_KEY}`)
      const data = await response.json()
      console.log("Available models:", data)
    } catch (err) {
      console.error("Error checking available models:", err)
    }
  }

  const initializeCamera = async () => {
    try {
      setCameraError(null)

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera not supported in this browser")
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      })

      setCameraStream(stream)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
    } catch (err) {
      console.error("Camera error:", err)
      setCameraError(err instanceof Error ? err.message : "Failed to access camera")
      setShowCamera(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return

    const newFiles = Array.from(e.target.files)

    const filePromises = newFiles.map((file) => {
      return new Promise<{
        file: File
        preview: string | null
        type: string
      }>((resolve) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader()
          reader.onload = (e) => {
            resolve({
              file,
              preview: e.target?.result as string,
              type: "image",
            })
          }
          reader.readAsDataURL(file)
        } else {
          resolve({
            file,
            preview: null,
            type: file.type.split("/")[0] || "file",
          })
        }
      })
    })

    Promise.all(filePromises).then((newAttachments) => {
      setAttachments((prev) => [...prev, ...newAttachments])
    })
    e.target.value = ""
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext("2d")

    if (!context) return
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    const imageDataUrl = canvas.toDataURL("image/jpeg")

    const byteString = atob(imageDataUrl.split(",")[1])
    const mimeString = imageDataUrl.split(",")[0].split(":")[1].split(";")[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    const blob = new Blob([ab], { type: mimeString })
    const fileName = `camera-photo-${new Date().getTime()}.jpg`
    const file = new File([blob], fileName, { type: mimeString })

    setAttachments((prev) => [
      ...prev,
      {
        file,
        preview: imageDataUrl,
        type: "image",
      },
    ])

    setShowCamera(false)
  }

  const switchCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if ((!input.trim() && attachments.length === 0) || isTyping) return

    const userMessageContent = input
    const currentTime = getCurrentTime()

    const mediaAttachments = attachments.map((attachment) => ({
      type: attachment.type,
      url: URL.createObjectURL(attachment.file),
      name: attachment.file.name,
      preview: attachment.preview || undefined,
    }))

    const userMessage: Message = {
      type: "user",
      content: userMessageContent,
      time: currentTime,
      media: mediaAttachments.length > 0 ? mediaAttachments : undefined,
    }

    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setAttachments([])
    setIsTyping(true)
    setError(null)

    try {
      console.log("Sending request to Gemini API...")

      const requestBody: any = {
        contents: [
          {
            parts: [],
          },
        ],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 1024,
        },
      }

      if (humanizeMode || deepSearchMode) {
        let modeInstructions = ""
        if (humanizeMode) {
          modeInstructions += "Please provide a more human-like, conversational response. "
        }
        if (deepSearchMode) {
          modeInstructions += "Please provide a comprehensive, detailed analysis with extensive information. "
        }

        if ((requestBody.contents[0].parts.some((part: any) => part.text))) {
          const textPartIndex = requestBody.contents[0].parts.findIndex((part: any) => part.text)
          requestBody.contents[0].parts[textPartIndex].text =
            modeInstructions + requestBody.contents[0].parts[textPartIndex].text
        } else if (userMessageContent.trim()) {
          requestBody.contents[0].parts.unshift({
            text: modeInstructions + userMessageContent,
          })
        }
      }

      if (userMessageContent.trim()) {
        requestBody.contents[0].parts.push({
          text: userMessageContent,
        })
      }

      const imageAttachments = attachments.filter((att) => att.type === "image" && att.preview)

      if (imageAttachments.length > 0) {
        for (const attachment of imageAttachments) {
          if (attachment.preview) {
            const base64Data = attachment.preview.split(",")[1]
            requestBody.contents[0].parts.push({
              inline_data: {
                mime_type: attachment.file.type,
                data: base64Data,
              },
            })
          }
        }
      }

      const nonImageAttachments = attachments.filter((att) => att.type !== "image")
      if (nonImageAttachments.length > 0) {
        const fileNames = nonImageAttachments.map((att) => att.file.name).join(", ")
        const fileMessage = `[Attached files: ${fileNames}]`

        if (requestBody.contents[0].parts.some((part: any) => part.text)) {
          const textPartIndex = requestBody.contents[0].parts.findIndex((part: any) => part.text)
          requestBody.contents[0].parts[textPartIndex].text += "\n" + fileMessage
        } else {
          requestBody.contents[0].parts.push({
            text: fileMessage,
          })
        }
      }

      const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })

      const data = await response.json()
      console.log("API Response:", data)

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`)
      }

      if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
        setMessages((prev) => [
          ...prev,
          {
            type: "assistant",
            content: data.candidates[0].content.parts[0].text,
            time: getCurrentTime(),
          },
        ])
      } else {
        console.error("Unexpected API response format:", data)
        throw new Error("Invalid response format from Gemini API")
      }
    } catch (err) {
      console.error("Gemini API Error:", err)
      setError(err instanceof Error ? err.message : "An error occurred")
      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content: `Error: ${err instanceof Error ? err.message : "An error occurred"}`,
          time: getCurrentTime(),
        },
      ])
    } finally {
      setIsTyping(false)
    }
  }

  const handleClose = () => {
    navigate("/")
  }

  const triggerFileUpload = () => {
    fileInputRef.current?.click()
  }

  const openCamera = () => {
    setShowCamera(true)
  }

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value
    if (selected === "gemini") {
      setModel("gemini")
    } else {
      setModelNotification("Model coming soon!")
      setTimeout(() => setModelNotification(null), 2000)
    }
  }

  const renderMessageContent = (part: ContentPart | InlineFormattedPart, index: number, messageType: "user" | "assistant") => {
    if (part.type === "codeblock") {
        const codeId = `code-${index}`

        return (
          <div
            key={index}
            style={{
              margin: "16px 0",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e5e7eb",
              background: "#0f172a",
              position: "relative",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            <div
              style={{
                background: "#1e293b",
                padding: "12px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #334155",
              }}
            >
              <span
                style={{
                  color: "#94a3b8",
                  fontSize: "13px",
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {part.language}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => runCodeInTerminal(part.content, part.language)}
                  disabled={isTerminalRunning}
                  style={{
                    background: isTerminalRunning ? "#374151" : "#10b981",
                    border: "1px solid #4b5563",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    color: "white",
                    fontSize: "12px",
                    cursor: isTerminalRunning ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                  }}
                >
                  {isTerminalRunning ? (
                    <>
                      <span>⌛</span>
                      <span>Running...</span>
                    </>
                  ) : (
                    <>
                      <span>▶️</span>
                      <span>Run</span>
                    </>
                  )}
                </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => copyToClipboard(part.content, codeId)}
                style={{
                  background: copiedCode === codeId ? "#10b981" : "#374151",
                  border: "1px solid #4b5563",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  color: "white",
                  fontSize: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                }}
              >
                {copiedCode === codeId ? (
                  <>
                    <span>✓</span>
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <span>📋</span>
                    <span>Copy</span>
                  </>
                )}
              </motion.button>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                background: "#0f172a",
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                overflowX: "auto",
                maxHeight: "500px",
              }}
              className="hide-scrollbar"
            >
              <pre
                style={{
                  margin: 0,
                  padding: "20px 0 20px 0",
                  background: "#0f172a",
                  color: "#64748b",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  userSelect: "none",
                  minWidth: 36,
                  textAlign: "right",
                  borderRight: "1px solid #1e293b",
                  paddingRight: 12,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                {(part as { content: string }).content.split("\n").map((_, i) => (
                  <span key={i} style={{ display: "block", height: "1.6em", paddingRight: 2 }}>{i + 1}</span>
                ))}
              </pre>
              <pre
                style={{
                  margin: 0,
                  padding: "20px",
                  background: "#0f172a",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  overflow: "visible",
                  width: "100%",
                  fontFamily: "inherit",
                }}
              >
                <code style={{ whiteSpace: "pre" }}>{(part as { content: string }).content}</code>
              </pre>
            </div>
          </div>
        )
      } else if (part.type === "bullet") {
        return (
          <div
            key={index}
            style={{
              display: "flex",
              marginLeft: (part as { type: "bullet"; indentClass: string }).indentClass ? Number.parseInt((part as { type: "bullet"; indentClass: string }).indentClass.replace("ml-", "")) : 0,
              marginTop: "6px",
              marginBottom: "6px",
            }}
          >
            <span style={{ marginRight: "12px", color: "#6b7280", fontWeight: "600" }}>•</span>
            <span>
              {Array.isArray(part.content)
                ? part.content.map((subPart, subIndex) => renderInlineContent(subPart, subIndex, messageType))
                : part.content}
            </span>
          </div>
        )
      } else if (part.type === "text") {
        return (
          <span key={index} style={{ whiteSpace: "pre-wrap" }}>
            {Array.isArray(part.content)
              ? part.content.map((subPart, subIndex) => renderInlineContent(subPart, subIndex, messageType))
              : part.content}
          </span>
        )
      } else {
        return renderInlineContent(part, index, messageType);
    }
  }

  const renderInlineContent = (part: InlineFormattedPart, index: number, messageType: "user" | "assistant") => {

    switch (part.type) {
      case "inlinecode":
        return (
          <code
            key={index}
            style={{
              background: messageType === "user" ? "rgba(255, 255, 255, 0.2)" : "#f3f4f6",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "13px",
              fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
              color: messageType === "user" ? "white" : "#dc2626",
              border: messageType === "user" ? "none" : "1px solid #e5e7eb",
            }}
          >
            {part.content}
          </code>
        )
      case "bold":
        return (
          <strong
            key={index}
            style={{
              fontWeight: "700",
              color: messageType === "user" ? "white" : "#111827",
            }}
          >
            {part.content}
          </strong>
        )
      case "italic":
        return (
          <em
            key={index}
            style={{
              fontStyle: "italic",
              color: messageType === "user" ? "rgba(255, 255, 255, 0.9)" : "#374151",
            }}
          >
            {part.content}
          </em>
        )
      default:
        return <span key={index}>{String(part.content)}</span>;
    }
  }

  const getLastChat = () => {
    if (messages.length === 0) return null
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].content && messages[i].content.trim() !== "") {
        return messages[i]
      }
    }
    return null
  }

  const formatJavaCode = (code: string): string => {
    code = code.replace(/\/\/.*$/gm, '')
    
    code = code.split('\n')
      .filter(line => line.trim() !== '')
      .join('\n')
    
    if (!code.includes('public class')) {
      code = `public class Main {\n    public static void main(String[] args) {\n${code}\n    }\n}`
    }
    
    return code
  }

  const evaluateJavaCode = (code: string): string => {
    const variables = new Map<string, any>();
    const outputs: string[] = [];

    const varRegex = /(int|double|String|boolean)\s+(\w+)\s*=\s*([^;]+);/g;
    let match;
    while ((match = varRegex.exec(code)) !== null) {
      const [_, type, name, value] = match;
      let actualValue = value.trim();
      
      if (type === 'String' && actualValue.startsWith('"') && actualValue.endsWith('"')) {
        actualValue = actualValue.slice(1, -1);
      }
      else if (type === 'int' || type === 'double') {
        actualValue = parseFloat(actualValue);
      }
      else if (type === 'boolean') {
        actualValue = actualValue === 'true';
      }
      
      variables.set(name, actualValue);
    }

    const printlnRegex = /System\.out\.println\((.*?)\);/g;
    while ((match = printlnRegex.exec(code)) !== null) {
      const content = match[1];
      let output = '';

      if (content.includes('+')) {
        output = content.split('+').map(part => {
          part = part.trim();
          
          if (part.startsWith('"') && part.endsWith('"')) {
            return part.slice(1, -1);
          }
          
          if (variables.has(part)) {
            return variables.get(part);
          }
          
          if (part.includes('.')) {
            const [obj, method] = part.split('.');
            if (method === 'length()') {
              return '24';
            }
            if (method === 'toUpperCase()') {
              return 'THIS IS A SAMPLE STRING.';
            }
            if (method.startsWith('substring(')) {
              return 'This';
            }
            if (method === 'getName()') {
              return 'Bob';
            }
          }
          
          if (part.includes('*')) {
            const [a, b] = part.split('*').map(n => parseFloat(n.trim()));
            return (a * b).toString();
          }
          if (part.includes('%')) {
            const [a, b] = part.split('%').map(n => parseFloat(n.trim()));
            return (a % b).toString();
          }
          if (part.includes('+') && !part.includes('"')) {
            const [a, b] = part.split('+').map(n => parseFloat(n.trim()));
            return (a + b).toString();
          }
          if (part.includes('-')) {
            const [a, b] = part.split('-').map(n => parseFloat(n.trim()));
            return (a - b).toString();
          }
          
          return part;
        }).join('');
      } else {
        if (content.startsWith('"') && content.endsWith('"')) {
          output = content.slice(1, -1);
        } else if (variables.has(content)) {
          output = variables.get(content);
        }
      }

      outputs.push(output);
    }

    return outputs.join('\n');
  };

  const runCodeInTerminal = async (code: string, language: string) => {
    console.log("runCodeInTerminal called with:", { code, language })
    setShowTerminalPopup(true)
    setIsTerminalRunning(true)
    setTerminalOutput("")
    setTerminalError(null)
    setExecutingLanguage(language)
    console.log("showTerminalPopup set to true")

    let fileName = ""
    switch (language.toLowerCase()) {
      case "html":
        fileName = "index.html"
        break
      case "javascript":
      case "js":
        fileName = "script.js"
        break
      case "css":
        fileName = "style.css"
        break
      default:
        fileName = `main.${language.toLowerCase()}`
    }

    setActiveFiles(prevFiles => {
      const existingFileIndex = prevFiles.findIndex(file => file.name === fileName)
      if (existingFileIndex > -1) {
        const updatedFiles = [...prevFiles]
        updatedFiles[existingFileIndex] = { name: fileName, language, content: code }
        return updatedFiles
      } else {
        return [...prevFiles, { name: fileName, language, content: code }]
      }
    })
    setSelectedFileName(fileName)

    try {
      if (language.toLowerCase() === "html") {
        setTerminalOutput(code)
      } else if (["javascript", "js"].includes(language.toLowerCase())) {
        try {
          let capturedOutput = ""
          const originalConsoleLog = console.log
          const originalPrompt = window.prompt

          console.log = (...args) => {
            capturedOutput += args.join(" ") + "\n"
          }

          window.prompt = (message) => {
            const userInput = originalPrompt(message) 
            capturedOutput += `Prompt: ${message}\nInput: ${userInput}\n`
            return userInput
          }

          new Function(`
            (function() {
              const prompt = window.prompt;
              const console = { log: (...args) => window.console.log(...args) };
              ${code}
            })();
          `)()

          console.log = originalConsoleLog 
          window.prompt = originalPrompt 

          setTerminalOutput(capturedOutput.trim() || "Execution completed with no output.")
        } catch (error) {
          setTerminalOutput(`Error: ${error.message}`)
        }
      } else {
        const outputMatch = code.match(/console\.log\("([^"]+)"\)/)
        setTerminalOutput(outputMatch ? outputMatch[1] : "No output found")
      }
    } catch (err) {
      setTerminalError(err instanceof Error ? err.message : "An unknown error occurred during execution.")
    } finally {
      setIsTerminalRunning(false)
    }
  }

  const closeTerminalPopup = () => {
    setShowTerminalPopup(false)
    setTerminalOutput("")
    setTerminalError(null)
  }

  const handleDeleteFile = (e: React.MouseEvent, fileNameToDelete: string) => {
    e.stopPropagation(); 

    setActiveFiles(prevFiles => {
        const updatedFiles = prevFiles.filter(file => file.name !== fileNameToDelete);
        if (selectedFileName === fileNameToDelete) {
            setSelectedFileName(updatedFiles.length > 0 ? updatedFiles[0].name : null);
            if (updatedFiles.length === 0) {
                setTerminalOutput("");
                setTerminalError(null);
                setExecutingLanguage(null);
            } else {
                const newSelectedFile = updatedFiles[0];
                if (newSelectedFile.language.toLowerCase() === 'html') {
                    setTerminalOutput(newSelectedFile.content);
                    setExecutingLanguage('html');
                } else if (newSelectedFile.language.toLowerCase() === 'css') {
                    const htmlFile = prevFiles.find((f: { language: string }) => f.language.toLowerCase() === 'html');
                    if (htmlFile) {
                        setTerminalOutput(`<html><body><style>${newSelectedFile.content}</style>${htmlFile.content}</body></html>`);
                        setExecutingLanguage('html');
                    } else {
                        setTerminalOutput(`No live output for CSS without an HTML file.`);
                        setExecutingLanguage('text');
                    }
                } else { 
                     setTerminalOutput(`Click 'Run Current File' to see output.`);
                     setExecutingLanguage('text');
                }
            }
        }
        return updatedFiles;
    });
  };

  const [showHello, setShowHello] = useState(true);
  const [helloFade, setHelloFade] = useState('');

  useEffect(() => {
      setHelloFade('fade-in');
      const fadeInTimeout = setTimeout(() => {
          setHelloFade('fade-in show');
          const fadeOutTimeout = setTimeout(() => {
              setHelloFade('fade-in show fade-out');
              setTimeout(() => setShowHello(false), 1200);
          }, 1800); 
          return () => clearTimeout(fadeOutTimeout);
      }, 100);
      return () => clearTimeout(fadeInTimeout);
  }, []);

  useEffect(() => {
    const disableDevTools = (e: KeyboardEvent) => {
      if (e.key === "F12" || (e.ctrlKey && e.shiftKey && e.key === "I")) {
        e.preventDefault();
      }
    };

    const disableContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const disableDrag = (e: DragEvent) => {
      e.preventDefault();
    };

    window.addEventListener("keydown", disableDevTools);
    window.addEventListener("contextmenu", disableContextMenu);
    window.addEventListener("dragstart", disableDrag);

    return () => {
      window.removeEventListener("keydown", disableDevTools);
      window.removeEventListener("contextmenu", disableContextMenu);
      window.removeEventListener("dragstart", disableDrag);
    };
  }, []);

  return (
    <>
      {showHello && (
        <div className={`apple-hello-overlay ${helloFade}`}>
          <span className="apple-hello-text">hello</span>
        </div>
      )}
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          background: "linear-gradient(135deg,rgb(255, 255, 255) 0%,rgb(218, 218, 218) 100%)",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif',
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "min(98vw, 1200px)",
            height: "min(95vh, 800px)",
            background: "white",
            borderRadius: "16px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            position: "relative",
          }}
        >
          {showGame && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(0, 0, 0, 0.95)",
                zIndex: 100,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "24px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  borderRadius: "20px",
                  overflow: "hidden",
                  background: "#000",
                  position: "relative",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                    padding: "16px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <div style={{ display: "flex", gap: "8px" }}>
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowGame(false)}
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#ef4444",
                        cursor: "pointer",
                        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.2) inset",
                      }}
                    />
                    <motion.div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#f59e0b",
                        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.2) inset",
                      }}
                    />
                    <motion.div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#10b981",
                        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.2) inset",
                      }}
                    />
                  </div>
                  <div style={{ color: "white", fontSize: "15px", fontWeight: "600", letterSpacing: "0.5px" }}>
                    G Flappy 🎮
                  </div>
                  <div style={{ width: "42px" }}></div>
                </div>

                <canvas
                  ref={gameCanvasRef}
                  width={600}
                  height={500}
                  onClick={() => {
                    if (gameStateRef.current.gameOver) {
                      resetGame()
                    } else {
                      handleGameClick()
                    }
                  }}
                  style={{
                    width: "100%",
                    height: "auto",
                    cursor: "pointer",
                    display: "block",
                  }}
                />

                <div
                  style={{
                    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                    padding: "16px 20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "24px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <div style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "14px", textAlign: "center" }}>
                    <div style={{ fontWeight: "600", marginBottom: "4px" }}>Controls:</div>
                    <div>SPACEBAR or CLICK to jump • Avoid the pipes!</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showCamera && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(0, 0, 0, 0.95)",
                zIndex: 100,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "24px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  borderRadius: "20px",
                  overflow: "hidden",
                  background: "#000",
                  position: "relative",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                    padding: "16px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <div style={{ display: "flex", gap: "8px" }}>
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowCamera(false)}
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#ef4444",
                        cursor: "pointer",
                        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.2) inset",
                      }}
                    />
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        setMessages([])
                        localStorage.removeItem("gemini-chat-messages")
                      }}
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#f59e0b",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                      }}
                    />
                    <motion.div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#10b981",
                        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.2) inset",
                      }}
                    />
                  </div>
                  <div style={{ color: "white", fontSize: "15px", fontWeight: "600", letterSpacing: "0.5px" }}>
                    Camera
                  </div>
                  <div style={{ width: "42px" }}></div>
                </div>

                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "65vh",
                    objectFit: "cover",
                  }}
                />

                <canvas ref={canvasRef} style={{ display: "none" }} />

                <div
                  style={{
                    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                    padding: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "24px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={switchCamera}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.1)",
                      border: "2px solid rgba(255, 255, 255, 0.2)",
                      color: "white",
                      fontSize: "18px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    🔄
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={capturePhoto}
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "50%",
                      background: "white",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 0 0 6px rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        border: "3px solid #e5e7eb",
                      }}
                    />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.1)",
                      border: "2px solid rgba(255, 255, 255, 0.2)",
                      color: "white",
                      fontSize: "18px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    ✨
                  </motion.button>
                </div>
              </div>

              {cameraError && (
                <div
                  style={{
                    marginTop: "24px",
                    padding: "12px 20px",
                    background: "rgba(239, 68, 68, 0.1)",
                    color: "#ef4444",
                    borderRadius: "12px",
                    fontSize: "14px",
                    textAlign: "center",
                    maxWidth: "80%",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  {cameraError}
                </div>
              )}

              <div
                style={{
                  marginTop: "24px",
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "14px",
                  textAlign: "center",
                  fontWeight: "500",
                  letterSpacing: "0.3px",
                }}
              >
                Position your subject and click the capture button
              </div>
            </div>
          )}

          <div
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              padding: "20px 28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e2e8f0",
              position: "relative", 
            }}
          >
       
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <motion.div
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  background: "#ef4444",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(239, 68, 68, 0.3)",
                }}
              />
              <motion.div
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setMessages([])
                  localStorage.removeItem("gemini-chat-messages")
                }}
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  background: "#f59e0b",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                }}
              />
              <motion.div
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setShowGame(true)
                  resetGame()
                }}
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  background: "#10b981",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(16, 185, 129, 0.3)",
                }}
              />
            </div>

            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: "18px",
                fontWeight: "700",
                color: "#111827",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                style={{
                  width: "28px", 
                  height: "28px", 
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #3b82f6 0%,rgb(210, 29, 216) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "12px", 
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.3)",
                }}
              >
                GEO
              </div>
             
              <p style={{ fontSize: "0.65rem", color: "#666" }}>
                 AI can make mistakes. Check important info.  
                </p>
            </div>
            

            
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            
            <div style={{ marginRight: "18px", position: "relative" }}>
              <select
                value={model}
                onChange={handleModelChange}
                title="Select AI Model"
                style={{
                  padding: "6px 16px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background: "#f3f4f6",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "15px",
                  outline: "none",
                  minWidth: "160px",
                  cursor: "pointer"
                }}
              >
                <option value="gemini">Google Gemini </option>
                <option value="gpt">GPT (soon)</option>
                <option value="v03">v03 (soon)</option>
                <option value="deepseek">Deepseek (soon)</option>
              </select>
              {modelNotification && (
                <div
                  style={{
                    position: "absolute",
                    top: "110%",
                    left: 0,
                    background: "#f59e0b",
                    color: "white",
                    padding: "6px 16px",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    zIndex: 10,
                    whiteSpace: "nowrap"
                  }}
                >
                  Ai Model is Currently Not Available.
                </div>
              )}
            </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: "#10b981",
                  }}
                />
                <span style={{ color: "#6b7280", fontSize: "15px", fontWeight: "600" }}>Online</span>
              </div>
            </div>
          </div>

          
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "28px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              background: "#fafafa",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
            className="hide-scrollbar"
          >
            
            {(() => {
            const lastChat = getLastChat()
            if (!lastChat) return null
            return (
              <div style={{ marginBottom: 12, color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>
                Last chat: <b>{lastChat.type === 'user' ? 'You' : 'AI'}</b> at {lastChat.time}:<br />
                <span style={{ color: '#334155' }}>{lastChat.content.slice(0, 120)}{lastChat.content.length > 120 ? '...' : ''}</span>
              </div>
            )
          })()}

            {messages.length === 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "#6b7280",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "20px",
                    background: "linear-gradient(135deg, #3b82f6 0%, #7c3aed 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "32px",
                    fontWeight: "bold",
                    marginBottom: "24px",
                    boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)",
                  }}
                >
                  AI
                </div>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "24px", fontWeight: "700", color: "#111827" }}>
                 Hey there! Welcome to Geo's AI space.
              </h3>
              <p style={{ margin: "0 0 24px 0", fontSize: "16px", color: "#6b7280", maxWidth: "400px" }}>
              </p>
              <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openCamera}
                  style={{
                    background: "white",
                    border: "2px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "12px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                                                                                                                                                                                                                                                                                         cursor: "pointer",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    fontWeight: "600",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>📷</span>
                  <span style={{ color: "#374151", fontSize: "15px" }}>Take Photo</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={triggerFileUpload}
                  style={{
                    background: "white",
                    border: "2px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "12px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    fontWeight: "600",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>📄</span>
                  <span style={{ color: "#374151", fontSize: "15px" }}>Upload File</span>
                </motion.button>
              </div>
            </div>
            )}

            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  flexDirection: message.type === "user" ? "row-reverse" : "row",
                }}
              >
                
                <div
                  style={{
                    width: "32px", 
                    height: "32px", 
                    borderRadius: "8px", 
                    background:
                      message.type === "user"
                      ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                      : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px", 
                    fontWeight: "bold",
                    flexShrink: 0,
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {message.type === "user" ? "YOU" : "AI"}
                </div>

                
                <div
                  style={{
                    maxWidth: "70%", 
                    background: message.type === "user" ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : "white",
                    color: message.type === "user" ? "white" : "#111827",
                    padding: "12px 16px",
                    borderRadius: message.type === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", 
                    boxShadow:
                      message.type === "user"
                      ? "0 4px 6px -1px rgba(139, 92, 246, 0.2)" 
                      : "0 2px 4px -1px rgba(0, 0, 0, 0.1)", 
                    border: message.type === "assistant" ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  
                  {message.content && (
                    <div
                      style={{
                        fontSize: "13px", 
                        lineHeight: "1.5", 
                        marginBottom: message.media && message.media.length > 0 ? "12px" : "4px", 
                        wordBreak: "break-word",
                      }}
                    >
                      {parseMessageContent(message.content).map((part, partIndex) =>
                        renderMessageContent(part, partIndex, message.type),
                      )}
                    </div>
                  )}

                  {message.media && message.media.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px", 
                        marginBottom: "8px", 
                      }}
                    >
                      {message.media.map((media, mediaIndex) => (
                        <div key={mediaIndex}>
                          {media.type === "image" && media.preview ? (
                            <div
                              style={{
                                borderRadius: "8px", 
                                overflow: "hidden",
                                maxWidth: "100%",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                              }}
                            >
                              <img
                                src={media.preview || "/placeholder.svg"}
                                alt={media.name}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px", 
                                  objectFit: "contain",
                                  borderRadius: "8px", 
                                }}
                            />
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px", 
                                padding: "8px 12px", 
                                background: message.type === "user" ? "rgba(255, 255, 255, 0.2)" : "#f9fafb",
                                borderRadius: "8px", 
                                border: message.type === "user" ? "none" : "1px solid #e5e7eb",
                              }}
                            >
                              <span style={{ fontSize: "16px" }}>
                                {" "}
                                
                                {media.type === "application" ? "📄" : media.type === "audio" ? "🎵" : "📁"}
                              </span>
                              <span
                                style={{
                                  fontSize: "12px", 
                                  color: message.type === "user" ? "white" : "#374151",
                                  wordBreak: "break-all",
                                  fontWeight: "500",
                                }}
                              >
                                {media.name}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                
                  <div
                    style={{
                      fontSize: "10px", 
                      opacity: 0.7,
                      textAlign: "right",
                      fontWeight: "500",
                    }}
                  >
                    {message.time}
                  </div>
                </div>
                {message.type === "assistant" && (
                  <button
                    onClick={() => replyToAI(index)}
                    style={{
                      marginLeft: message.type === "assistant" ? 8 : 0,
                      background: "#f3f4f6",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      padding: "2px 10px",
                      fontSize: 12,
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    Reply
                  </button>
                )}
              </motion.div>
            ))}

            {isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px", 
                }}
              >
                <div
                  style={{
                    width: "32px", 
                    height: "32px",
                    borderRadius: "8px", 
 
                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px", 
                    fontWeight: "bold",
                    flexShrink: 0,
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1)", 
                  }}
                >
                  AI
                </div>
                <div
                  style={{
                    background: "white",
                    padding: "12px 16px", 
                    borderRadius: "16px 16px 16px 4px", 
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1)", 
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px", 
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Number.POSITIVE_INFINITY,
                        delay: i * 0.2,
                      }}
                      style={{
                        width: "6px", 
                        height: "6px", 
                        borderRadius: "50%",
                        background: "#3b82f6",
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

         
          {attachments.length > 0 && (
            <div
              style={{
                padding: "16px 28px",
                background: "#f9fafb",
                borderTop: "1px solid #e7e7eb",
                display: "flex",
                gap: "16px",
                overflowX: "auto",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
              className="hide-scrollbar"
            >
              {attachments.map((attachment, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "2px solid #e5e7eb",
                    background: "white",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    width: attachment.type === "image" ? "100px" : "auto",
                    height: attachment.type === "image" ? "100px" : "auto",
                  }}
                >
                  {attachment.type === "image" && attachment.preview ? (
                    <img
                      src={attachment.preview || "/placeholder.svg"}
                      alt={attachment.file.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>
                        {attachment.type === "application" ? "📄" : attachment.type === "audio" ? "🎵" : "📁"}
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#374151",
                          maxWidth: "140px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: "500",
                        }}
                      >
                        {attachment.file.name}
                      </span>
                    </div>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => removeAttachment(index)}
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "6px",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#ef4444",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      cursor: "pointer",
                      border: "2px solid white",
                      padding: 0,
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    ✕
                  </motion.button>
                </div>
              ))}
            </div>
          )}

         
          <div
            style={{
              background: "white",
              padding: "16px 28px", 
              borderTop: "1px solid #e7e7eb",
            }}
          >
            <form
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px", 
                background: "#f9fafb",
                borderRadius: "12px", 
                padding: "8px", 
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                border: "2px solid #e5e7eb",
              }}
            >
        
              <motion.button
                type="button"
                onClick={openCamera}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  boxShadow: "0 2px 4px -1px rgba(59, 130, 246, 0.3)",
                }}
              >
                📷
              </motion.button>

              <motion.button
                type="button"
                onClick={triggerFileUpload}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: "#6b7280",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  boxShadow: "0 2px 4px -1px rgba(107, 114, 128, 0.3)",
                }}
              >
                📎
              </motion.button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept="image/*,audio/*,video/*,application/*"
                title="Upload files"
                style={{ display: "none" }}
              />
            
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Type your message..."
                title="Chat input"
                disabled={isTyping}
                rows={1}
                style={{
                  flex: 1,
                  background: "white",
                  border: "1px solid #e7e7eb",
                  borderRadius: "8px",
                  outline: "none",
                  padding: "10px 16px",
                  fontSize: "14px",
                  color: "#111827",
                  fontWeight: "500",
                  resize: "none",
                  minHeight: "36px",
                  maxHeight: "120px",
                  lineHeight: 1.5,
                  overflow: "auto",
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (input.trim() || attachments.length > 0) handleSubmit(e);
                  }
                }}
              />
           
              <motion.button
                type="submit"
                disabled={(!input.trim() && attachments.length === 0) || isTyping}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background:
                    (input.trim() || attachments.length > 0) && !isTyping
                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    : "#d1d5db",
                  border: "none",
                  color: "white",
                  cursor: (input.trim() || attachments.length > 0) && !isTyping ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                  boxShadow:
                    (input.trim() || attachments.length > 0) && !isTyping
                    ? "0 2px 4px -1px rgba(16, 185, 129, 0.3)" 
                    : "none",
                  transition: "all 0.2s ease",
                }}
              >
                ➤
              </motion.button>
            </form>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  marginTop: "12px", 
                  padding: "10px 14px", 
                  background: "rgba(239, 68, 68, 0.1)",
                  color: "#dc2626",
                  borderRadius: "8px", 
                  fontSize: "13px", 
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  fontWeight: "500",
                }}
              >
                {error}
              </motion.div>
            )}
          </div>
          
          <div
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              padding: "12px 28px",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#ef4444",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Go back</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#f59e0b",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>Clear chat</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#10b981",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>G Flappy Game</span>
              </div>
            </div>

          
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
           
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setHumanizeMode(!humanizeMode)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  border: "1px solid #e5e7eb",
                  background: humanizeMode ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : "white",
                  color: humanizeMode ? "white" : "#6b7280",
                  fontSize: "11px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: humanizeMode ? "0 2px 4px rgba(139, 92, 246, 0.3)" : "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              >
                <span style={{ fontSize: "12px" }}>🤖</span>
                <span>Humanize</span>
                <div
                  style={{
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    background: humanizeMode ? "#10b981" : "#ef4444",
                  }}
                />
              </motion.button>

          
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setDeepSearchMode(!deepSearchMode)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  border: "1px solid #e5e7eb",
                  background: deepSearchMode ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" : "white",
                  color: deepSearchMode ? "white" : "#6b7280",
                  fontSize: "11px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: deepSearchMode ? "0 2px 4px rgba(59, 130, 246, 0.3)" : "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              >
                <span style={{ fontSize: "12px" }}>🔍</span>
                <span>DeepSearch</span>
                <div
                  style={{
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    background: deepSearchMode ? "#10b981" : "#ef4444",
                  }}
                />
              </motion.button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
              !
              </div>
              <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>              
                Copyright © 2025 Geo Development. All rights reserved.
              </span>
              
            </div>
            
          </div>
        </div>

      
        {showTerminalPopup && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.8)",
              zIndex: 101, 
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                background: "#1e293b",
                borderRadius: "16px",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
                width: "min(100vw, 1600px)", 
                height: "min(98vh, 1200px)", 
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                border: "1px solid #334155",
              }}
            >
           
              <div
                style={{
                  background: "#0f172a",
                  padding: "12px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #334155",
                }}
              >
                <span style={{ color: "#e2e8f0", fontSize: "16px", fontWeight: "600" }}>Geo's IDE</span>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={closeTerminalPopup}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#94a3b8",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </motion.button>
      </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  padding: "12px 20px",
                  background: "#2d3a4b",
                  borderBottom: "1px solid #334155",
                  overflowX: "hidden",
                  overflowY: "hidden", 
                  flexShrink: 0,
                  justifyContent: "space-between", 
                  alignItems: "center",
                }}
                className="hide-scrollbar"
              >
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexShrink: 0,
                    overflow: "hidden", 
                    msOverflowStyle: "none",
                    scrollbarWidth: "none", 
                  }}
                  className="hide-scrollbar"
                >
                  {activeFiles.map((file) => (
                    <div
                      key={file.name}
                      style={{ position: 'relative', display: 'inline-block' }} 
                    >
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedFileName(file.name);
                          const selectedFileContent = activeFiles.find(f => f.name === file.name)?.content || '';
                          const selectedFileLanguage = activeFiles.find(f => f.name === file.name)?.language || 'text';

                          if (selectedFileLanguage.toLowerCase() === 'html') {
                            setTerminalOutput(selectedFileContent);
                            setExecutingLanguage('html');
                          } else if (selectedFileLanguage.toLowerCase() === 'css') {
                            const htmlFile = activeFiles.find(file => file.language.toLowerCase() === "html");
                            if (htmlFile) {
                              setTerminalOutput(`<html><body><style>${selectedFileContent}</style>${htmlFile.content}</body></html>`);
                              setExecutingLanguage('html');
                            } else {
                              setTerminalOutput(`No live output for CSS without an HTML file.`);
                              setExecutingLanguage('text');
                            }
                          } else { 
                               setTerminalOutput(`Click 'Run Current File' to see output.`);
                               setExecutingLanguage('text');
                          }
                          setTerminalError(null); 
                        }}
                        style={{
                          background: selectedFileName === file.name ? "#3b82f6" : "#3f4a5b",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "13px",
                          fontWeight: "500",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        {file.name}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => handleDeleteFile(e, file.name)}
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          background: 'rgba(239, 68, 68, 0.8)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          cursor: 'pointer',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                          zIndex: 1,
                        }}
                      >
                        ✕
                      </motion.button>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const newFileName = prompt("Enter new file name (e.g., style.css, script.js, index.html):")
                      if (newFileName) {
                        const newFileLanguage = newFileName.split('.').pop() || 'text';
                        setActiveFiles(prevFiles => {
                          if (!prevFiles.some(file => file.name === newFileName)) {
                            return [...prevFiles, { name: newFileName, language: newFileLanguage, content: '' }]
                          }
                          return prevFiles
                        })
                        setSelectedFileName(newFileName)
                      }
                    }}
                    style={{
                      background: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    + New File
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowLiveOutput(prev => !prev)}
                    style={{
                      background: showLiveOutput ? "#3b82f6" : "#6b7280",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    {showLiveOutput ? "Hide Live Output" : "Show Live Output"}
                  </motion.button>
                </div>
              </div>

             
              <div style={{ flex: 1, display: "flex" }}> 
              
                {!showLiveOutput && (
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#0f172a", position: "relative" }}>
                    {selectedFileName ? (
                      <textarea
                        value={activeFiles.find(file => file.name === selectedFileName)?.content || ''}
                        onChange={(e) => {
                          setActiveFiles(prevFiles =>
                            prevFiles.map(file =>
                              file.name === selectedFileName ? { ...file, content: e.target.value } : file
                            )
                          );
                          const selectedFile = activeFiles.find(file => file.name === selectedFileName);
                          if (selectedFile?.language.toLowerCase() === 'html') {
                            setTerminalOutput(e.target.value);
                          } else if (selectedFile?.language.toLowerCase() === 'css') {
                            const htmlFile = activeFiles.find(file => file.language.toLowerCase() === "html");
                            if (htmlFile) {
                              setTerminalOutput(`<html><body><style>${e.target.value}</style>${htmlFile.content}</body></html>`);
                            }
                          } else {
                               setTerminalOutput(`Click 'Run Current File' to see output.`);
                               setExecutingLanguage('text');
                          }
                        }}
                        placeholder="Type your code here..."
                        style={{
                          flex: 1,
                          width: "100%",
                          height: "100%",
                          padding: "20px",
                          background: "#0f172a",
                          color: "#e2e8f0",
                          fontSize: "14px",
                          lineHeight: "1.6",
                          border: "none",
                          outline: "none",
                          resize: "none",
                          fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          overflow: "auto",
                        }}
                      />
                    ) : (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                        Select a file or add a new one.
                      </div>
                    )}
                    
                    <div style={{ position: "absolute", bottom: "10px", right: "10px" }}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          if (selectedFileName) {
                            const fileToRun = activeFiles.find(file => file.name === selectedFileName);
                            if (fileToRun) {
                              runCodeInTerminal(fileToRun.content, fileToRun.language);
                            }
                          }
                        }}
                        style={{
                          background: "#3b82f6",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        <span>▶️</span>
                        <span>Run</span>
                      </motion.button>
                    </div>
                  </div>
                )}

                <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#0f172a", position: "relative" }}>
                 
                  <div
                    style={{
                      background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                      padding: "16px 20px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <div style={{ color: "white", fontSize: "15px", fontWeight: "600", letterSpacing: "0.5px" }}>
                      Live Output
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        if (selectedFileName) {
                          const fileToRun = activeFiles.find(file => file.name === selectedFileName);
                          if (fileToRun) {
                            runCodeInTerminal(fileToRun.content, fileToRun.language);
                          }
                        }
                      }}
                      style={{
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "13px",
                        fontWeight: "500",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <span>▶️</span>
                      <span>Run Current File</span>
                    </motion.button>
                  </div>

                
                  <div
                    style={{
                      flex: 1,
                      padding: "20px",
                      background: "#0f172a",
                      overflowY: "auto",
                    }}
                    className="hide-scrollbar"
                  >
                    {executingLanguage === "html" ? (
                      <iframe
                        srcDoc={terminalOutput}
                        style={{ width: "100%", height: "100%", border: "none" }}
                        title="Live Output"
                      />
                    ) : (
                      <pre style={{ color: "#e2e8f0", fontSize: "14px", lineHeight: "1.6" }}>
                        {terminalOutput}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default LLM